import { API } from '@ax/api';
import { API_SERVICES } from './utils';
import {
  ClinicianInfo,
  ProviderIntegrationInfo,
  ProviderProductOfferingResponse,
  ProviderResetPasswordInput,
  ProviderSettingsResponse,
} from './provider-interface';
import { DEVICE_TAG, DEVICE_VERSION_CODE, DEVICE_VERSION_NAME } from './common-constants.ts';

export const getProviderSettings = async (): Promise<ProviderSettingsResponse> => {
  const res = await API.get(API_SERVICES.PROVIDER_API, `/providers/me/settings`, {
    params: {
      deviceTag: DEVICE_TAG,
      deviceVersionCode: DEVICE_VERSION_CODE,
      deviceVersionName: DEVICE_VERSION_NAME,
    },
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const getProviderNoteTemplates = async (providerEmail: string): Promise<any> => {
  const res = await API.get(
    API_SERVICES.TEXT_EXPANDER_API,
    `athelas/templates?email=${providerEmail}`,
    {
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return res.data;
};

export const getProviderSettingsById = async (
  providerId: number,
): Promise<ProviderSettingsResponse> => {
  const res = await API.get(API_SERVICES.PROVIDER_API, `/providers/settings/${providerId}`, {
    params: {
      deviceTag: DEVICE_TAG,
      deviceVersionCode: DEVICE_VERSION_CODE,
      deviceVersionName: DEVICE_VERSION_NAME,
    },
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const getProviderProductOffering = async (): Promise<ProviderProductOfferingResponse> => {
  const res = await API.get(API_SERVICES.PROVIDER_API, `/providers/me/product-offering`, {
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const getProviderPreferenceTemplate = async (): Promise<any> => {
  const res = await API.get(
    API_SERVICES.PROVIDER_API,
    `/providers/me/clinician-preference-template`,
    {
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return res.data;
};

export const getProviderPreferenceData = async (): Promise<any> => {
  const res = await API.get(API_SERVICES.PROVIDER_API, `/providers/me/clinician-preference-data`, {
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const updateProviderPreferenceData = async (providerData: any[]): Promise<any> => {
  const res = await API.patch(
    API_SERVICES.PROVIDER_API,
    `/providers/me/clinician-preference-data`,
    {
      withCredentials: false,
      data: {
        preferences: providerData,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return res.data;
};

export const resetPassword = async (
  passwordInput: ProviderResetPasswordInput,
): Promise<unknown> => {
  const res = await API.post(API_SERVICES.PROVIDER_API, '/providers/me/password', {
    data: passwordInput,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return res.data;
};

export const getProviderIntegrationInfo = async (
  doctorId: string,
): Promise<ProviderIntegrationInfo> => {
  const res = await API.get(API_SERVICES.EHR_API, '/lynx/integration-info', {
    params: {
      doctorId,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data?.data;
};

export const setProviderPasscode = async (passcode: string): Promise<void> => {
  await API.post(
    API_SERVICES.PROVIDER_API,
    `/providers/me/devices/${DEVICE_TAG}/permutation-key?passcode=${passcode}`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

export const getClinicianInfo = async (searchText: string): Promise<ClinicianInfo[]> => {
  const res = await API.get(
    API_SERVICES.GWP_API,
    searchText === ''
      ? `/search?searchText&limit=999`
      : `/search?searchText=${searchText}&limit=999`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return res.data;
};
